import { useCallback, useEffect, useRef, useState } from "react";
import { DotsThreeVertical } from "@phosphor-icons/react";
import { useAuth } from "../../contexts/AuthContext";
import { tg, errorTg, codigoTg, guardarClave, leerClave, olvidarClave, cabeceraClave, nuevoNonce, reintentable, diaDe, etiquetaFecha } from "../../lib/telegram";
import TgMensaje, { TgAvatar } from "./TgMensaje";

const PAGINA = 30;
const mezclar = (a, b) => { const m = new Map(); [...a, ...b].forEach((x) => m.set(x.id, x)); return [...m.values()].sort((x, y) => x.id - y.id); };

/** Pide la clave de un chat «Solo yo» (queda SOLO en la memoria de esta pestaña) o la estrena al marcarlo. */
function FormClave({ titulo, boton, repetir, onListo, onCancelar, error, ocupado }) {
  const [k, setK] = useState("");
  const [k2, setK2] = useState("");
  const corta = k.length < 6;
  const distinta = repetir && k !== k2;
  return (
    <form className="tg-clave" data-testid="tg-clave-form" onSubmit={(e) => { e.preventDefault(); if (!corta && !distinta && !ocupado) onListo(k); }}>
      <b>{titulo}</b>
      <input className="tg-in" type="password" autoComplete="off" aria-label="Clave del chat" data-testid="tg-clave-input" placeholder="Clave (mínimo 6 caracteres)" value={k} maxLength={128} onChange={(e) => setK(e.target.value)} />
      {repetir && <input className="tg-in" type="password" autoComplete="off" aria-label="Repetir clave" data-testid="tg-clave-repetir" placeholder="Repite la clave" value={k2} maxLength={128} onChange={(e) => setK2(e.target.value)} />}
      {error && <div className="tg-err" role="alert">{error}</div>}
      <div className="tg-fila">
        <button type="submit" className="tg-btn pri" data-testid="tg-clave-ok" disabled={corta || distinta || ocupado}>{boton}</button>
        {onCancelar && <button type="button" className="tg-btn" onClick={onCancelar}>Cancelar</button>}
      </div>
    </form>
  );
}

/** Una conversación: cabecera, mensajes (lo más nuevo abajo), «Cargar anteriores» y caja de escribir (solo con permiso Y envío encendido). */
export default function TgConversacion({ chat, owner, esDueno, sendEnabled, onQuien, onAparte, onCambioChats, compacto }) {
  const { user } = useAuth();
  const id = chat.chat_id;
  const [abierto, setAbierto] = useState(() => !chat.locked || !!leerClave(owner, id));
  const [items, setItems] = useState([]);
  const [mas, setMas] = useState({ hay: false, antes: null });
  const [estado, setEstado] = useState("cargando");   // cargando | ok | error
  const [error, setError] = useState("");
  const [errClave, setErrClave] = useState("");
  const [texto, setTexto] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [errEnvio, setErrEnvio] = useState("");
  const [marcando, setMarcando] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [menuAbierto, setMenuAbierto] = useState(false);
  const nonceRef = useRef(null);
  const scrollRef = useRef(null);
  const tok = useRef(0);
  const vivo = useRef(true);
  useEffect(() => { vivo.current = true; return () => { vivo.current = false; }; }, []);

  const opciones = useCallback((extra = {}) => {
    const headers = cabeceraClave(owner, id);
    return { params: { ...(owner ? { owner_id: owner } : {}), ...extra }, ...(headers ? { headers } : {}) };
  }, [owner, id]);

  const fallaClave = useCallback((e) => {
    const c = codigoTg(e);
    if (c === "solo_yo_bad_key" || c === "solo_yo_key_required") { olvidarClave(owner, id); setAbierto(false); setErrClave(errorTg(e)); return true; }
    if (c === "solo_yo_locked") { setErrClave(errorTg(e)); setAbierto(false); return true; }
    return false;
  }, [owner, id]);

  // carga inicial (el padre monta esta pieza con key=dueño+chat: cambiar de chat = estado limpio)
  useEffect(() => {
    if (!abierto) { setEstado("ok"); return undefined; }
    const mio = ++tok.current;
    setEstado("cargando"); setError("");
    tg.get(`/chats/${encodeURIComponent(id)}/messages`, opciones({ limit: PAGINA }))
      .then((r) => {
        if (!vivo.current || mio !== tok.current) return;
        const d = r.data || {};
        setItems(mezclar([], d.items || [])); setMas({ hay: !!d.has_more, antes: d.next_before_id ?? null }); setEstado("ok");
      })
      .catch((e) => {
        if (!vivo.current || mio !== tok.current) return;
        if (fallaClave(e)) return;
        setEstado("error"); setError(errorTg(e));
      });
    return () => { tok.current += 1; };
  }, [abierto, id, owner, opciones, fallaClave]);

  // mensaje nuevo (cambió el último de la lista): pedir solo la página más reciente y mezclar
  const ultimo = chat.last?.id;
  const primera = useRef(true);
  useEffect(() => {
    if (primera.current) { primera.current = false; return; }
    if (!abierto) return;
    tg.get(`/chats/${encodeURIComponent(id)}/messages`, opciones({ limit: PAGINA }))
      .then((r) => { if (vivo.current) setItems((l) => mezclar(l, r.data?.items || [])); })
      .catch((e) => { if (vivo.current) fallaClave(e); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ultimo]);

  const ultimoId = items.length ? items[items.length - 1].id : 0;
  useEffect(() => { const el = scrollRef.current; if (el) el.scrollTop = el.scrollHeight; }, [ultimoId, estado]);

  const anteriores = async () => {
    if (!mas.hay || ocupado) return;
    setOcupado(true);
    try {
      const r = await tg.get(`/chats/${encodeURIComponent(id)}/messages`, opciones({ limit: PAGINA, before_id: mas.antes }));
      if (!vivo.current) return;
      setItems((l) => mezclar(l, r.data?.items || [])); setMas({ hay: !!r.data?.has_more, antes: r.data?.next_before_id ?? null });
    } catch (e) { if (vivo.current && !fallaClave(e)) setError(errorTg(e)); }
    if (vivo.current) setOcupado(false);
  };

  const desbloquear = (k) => { guardarClave(owner, id, k); setErrClave(""); setAbierto(true); };

  const puedeEscribir = !!chat.can_write && !!sendEnabled && abierto;
  const enviar = async () => {
    const cuerpo = texto;
    if (enviando || !puedeEscribir || !cuerpo.trim()) return;
    // mismo texto = mismo intento (el servidor no duplica); texto distinto = mensaje nuevo = nonce nuevo
    if (!nonceRef.current || nonceRef.current.texto !== cuerpo) nonceRef.current = { texto: cuerpo, nonce: nuevoNonce() };
    setEnviando(true); setErrEnvio("");
    try {
      const headers = cabeceraClave(owner, id);
      await tg.post(`/chats/${encodeURIComponent(id)}/send`, { text: cuerpo, nonce: nonceRef.current.nonce, ...(owner ? { owner_id: owner } : {}) }, headers ? { headers } : undefined);
      if (!vivo.current) return;
      nonceRef.current = null; setTexto("");
      onCambioChats?.();
      tg.get(`/chats/${encodeURIComponent(id)}/messages`, opciones({ limit: PAGINA })).then((r) => { if (vivo.current) setItems((l) => mezclar(l, r.data?.items || [])); }).catch(() => {});
    } catch (e) {
      if (!vivo.current) return;
      if (!reintentable(e) && codigoTg(e) !== "send_in_progress" && codigoTg(e) !== "telegram_error") nonceRef.current = null;
      setErrEnvio(errorTg(e));
    }
    if (vivo.current) setEnviando(false);
  };

  const marcarSoloYo = async (k) => {
    setOcupado(true); setErrClave("");
    try {
      await tg.put(`/chats/${encodeURIComponent(id)}/solo-yo`, { key: k });
      guardarClave(owner, id, k); setMarcando(false); onCambioChats?.();
    } catch (e) { if (vivo.current) setErrClave(errorTg(e)); }
    if (vivo.current) setOcupado(false);
  };
  const quitarSoloYo = async () => {
    setOcupado(true);
    try {
      const headers = cabeceraClave(owner, id);
      await tg.delete(`/chats/${encodeURIComponent(id)}/solo-yo`, headers ? { headers } : undefined);
      olvidarClave(owner, id); onCambioChats?.();
    } catch (e) { if (vivo.current) setErrClave(errorTg(e)); }
    if (vivo.current) setOcupado(false);
  };

  const grupo = chat.folder === "groups";
  const nombre = user?.name || "tu nombre";
  const sub = chat.locked ? "Solo yo · con clave" : { groups: "Grupo", personal: "Chat personal", bots: "Bot" }[chat.folder] || "";

  return (
    <div className={`tg-conv${compacto ? " compacto" : ""}`} data-testid="tg-conversacion" data-chat={id}>
      <div className="tg-hd">
        <TgAvatar chat={chat} owner={owner} tam={compacto ? 30 : 40} />
        <div className="tg-hd-t"><b data-testid="tg-conv-titulo">{chat.title}</b><small>{sub}</small></div>
        <span className="tg-sp" />
        {!esDueno && (puedeEscribir ? <span className="tg-pill ok" data-testid="tg-puede-escribir">PUEDE ESCRIBIR</span> : <span className="tg-pill" data-testid="tg-solo-lectura">SOLO LECTURA</span>)}
        {onAparte && <button type="button" className="tg-hd-ic" data-testid="tg-conv-aparte" aria-label="Abrir en pantalla aparte" title="Abrir en pantalla aparte" onClick={() => onAparte(id)}>⧉</button>}
        {(esDueno || onAparte) && (
          <button type="button" className="tg-hd-ic" aria-label="Más opciones" aria-expanded={menuAbierto} data-testid="tg-conv-menu" onClick={() => setMenuAbierto((v) => !v)}>
            <DotsThreeVertical size={20} weight="bold" />
          </button>
        )}
        {esDueno && (
          <div className={`tg-menu-panel tg-menu-conv${menuAbierto ? " abierto" : ""}`} data-testid="tg-conv-menu-panel">
            {onQuien && <button type="button" className="tg-menu-item" data-testid="tg-quien-ve-que" onClick={() => { onQuien(); setMenuAbierto(false); }}>⚙ Quién ve qué</button>}
            {abierto && !chat.locked && <button type="button" className="tg-menu-item" data-testid="tg-marcar-soloyo" onClick={() => { setMarcando((v) => !v); setMenuAbierto(false); }}>🔒 Marcar «Solo yo»</button>}
            {abierto && chat.locked && <button type="button" className="tg-menu-item" data-testid="tg-quitar-soloyo" disabled={ocupado} onClick={() => { quitarSoloYo(); setMenuAbierto(false); }}>Quitar «Solo yo»</button>}
          </div>
        )}
      </div>

      {marcando && !chat.locked && (
        <FormClave titulo="Marcar «Solo yo»: nunca se comparte con nadie y se pide clave para abrirlo. Lo que ya habías compartido de este chat se quita." boton="Marcar «Solo yo»" repetir onListo={marcarSoloYo} onCancelar={() => { setMarcando(false); setErrClave(""); }} error={errClave} ocupado={ocupado} />
      )}

      {!abierto ? (
        <div className="tg-ms centrado">
          <FormClave titulo="Este chat es «Solo yo». Escribe su clave para abrirlo." boton="Abrir" onListo={desbloquear} error={errClave} />
        </div>
      ) : (
        <div className="tg-ms" ref={scrollRef} data-testid="tg-mensajes" role="log" aria-live="polite" aria-label="Mensajes del chat">
          {mas.hay && <button type="button" className="tg-btn sm tg-mas" data-testid="tg-anteriores" disabled={ocupado} onClick={anteriores}>Cargar anteriores</button>}
          {estado === "cargando" && !items.length && <div className="tg-nd centro" data-testid="tg-cargando">Cargando mensajes…</div>}
          {estado === "error" && <div className="tg-err" role="alert">{error}</div>}
          {estado === "ok" && !items.length && <div className="tg-nd centro">Aún no hay mensajes.</div>}
          {items.map((m, i) => {
            const nuevoDia = i === 0 || diaDe(m.ts) !== diaDe(items[i - 1].ts);
            return (
              <div key={m.id} style={{ display: "contents" }}>
                {nuevoDia && <div className="tg-fecha" data-testid="tg-fecha"><span>{etiquetaFecha(m.ts)}</span></div>}
                <TgMensaje m={m} grupo={grupo} />
              </div>
            );
          })}
          {!sendEnabled && <div className="tg-ro" data-testid="tg-modo-lectura">Modo lectura: por ahora solo se ve. El envío se activa cuando se ordene.</div>}
          {sendEnabled && !chat.can_write && <div className="tg-ro" data-testid="tg-sin-permiso">Tienes este chat en solo lectura: no tienes permiso de escribir.</div>}
        </div>
      )}

      <div className="tg-cm">
        <span className="tg-icono" title="Adjuntar archivos aún no está disponible" aria-hidden="true">📎</span>
        <textarea className="tg-in tg-caja" data-testid="tg-caja" rows={1} aria-label="Escribir un mensaje" disabled={!puedeEscribir || enviando} value={texto} maxLength={4000}
          placeholder={puedeEscribir ? "Escribe un mensaje…" : !sendEnabled ? "Escribe un mensaje… (desactivado en modo lectura)" : "Escribe un mensaje… (sin permiso en este chat)"}
          onChange={(e) => { setTexto(e.target.value); setErrEnvio(""); }}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); enviar(); } }} />
        <span className="tg-icono" title="Los emojis aún no están disponibles" aria-hidden="true">☺</span>
        {/* el micrófono pasa a flecha de enviar al escribir (igual que Telegram): los dos quedan montados, la
            clase decide cuál se ve -- el botón de enviar (`tg-enviar`) es siempre el mismo, con su misma lógica de siempre. */}
        <span className={`tg-icono${texto.trim() ? " tg-oculto" : ""}`} title="Las notas de voz aún no están disponibles" aria-hidden="true">🎤</span>
        <button type="button" className={`tg-enviar${texto.trim() ? "" : " tg-oculto"}`} data-testid="tg-enviar" aria-label="Enviar" disabled={!puedeEscribir || enviando || !texto.trim()} onClick={enviar}>➤</button>
      </div>
      {puedeEscribir && !esDueno && <div className="tg-firma" data-testid="tg-firma">Tu mensaje sale firmado «{nombre}: …» desde la cuenta del dueño; queda registrado quién lo escribió.</div>}
      {errEnvio && <div className="tg-err envio" role="alert" data-testid="tg-err-envio">{errEnvio}</div>}
    </div>
  );
}
